import React from 'react';
import { type AccessibilityRole, Platform, View } from 'react-native';

import { Skeleton, createMqStyles, tokens } from '@fhs/ui';

import { useStoreCardMqStyles } from '../store-card/store-card.styles';

const isWeb = Platform.OS === 'web';

// Enough rows to fill the panel at desktop height; the list clips whatever overflows.
const PLACEHOLDER_CARDS = [0, 1, 2, 3, 4];

interface StoreListSkeletonProps {
  ariaLabel: string;
}

/**
 * Placeholder rows shown while restaurants load. The geometry comes from the real card's
 * styles so the content lands in place instead of jumping when the results arrive.
 */
export const StoreListSkeleton: React.VFC<StoreListSkeletonProps> = ({ ariaLabel }) => {
  const cardStyles = useStoreCardMqStyles();
  const styles = useStoreListSkeletonMqStyles();

  return (
    <View
      testID="store-locator-loading"
      style={styles.list}
      accessible
      accessibilityLabel={ariaLabel}
      accessibilityRole={(isWeb ? 'status' : 'text') as AccessibilityRole}
      accessibilityLiveRegion="polite"
      {...(isWeb
        ? ({ role: 'status', 'aria-live': 'polite', 'aria-busy': true } as Record<string, unknown>)
        : {})}
    >
      {PLACEHOLDER_CARDS.map(index => (
        <View key={index} style={cardStyles.card}>
          <View style={cardStyles.cardBody}>
            <View style={cardStyles.bodyRow}>
              <View style={styles.details}>
                <Skeleton style={[styles.line, styles.lineName]} />
                <Skeleton style={[styles.line, styles.lineAddress]} />
                <Skeleton style={[styles.line, styles.lineStatus]} />
              </View>

              <View style={cardStyles.ctaColumn}>
                <View style={cardStyles.iconRow}>
                  <Skeleton style={styles.icon} />
                  <Skeleton style={styles.icon} />
                </View>
                <Skeleton style={styles.cta} />
              </View>
            </View>
          </View>
          <View style={cardStyles.divider} />
        </View>
      ))}
    </View>
  );
};

const useStoreListSkeletonMqStyles = createMqStyles({
  list: {
    $base: {
      flex: 1,
      overflow: 'hidden',
    },
  },
  // Mirrors the card's select region so the placeholder lines share its vertical rhythm.
  details: {
    $base: {
      flexGrow: 1,
      flexShrink: 1,
      gap: 6,
      justifyContent: 'center',
    },
    $gteDesktop: {
      gap: 8,
    },
  },
  line: {
    $base: {
      backgroundColor: tokens.colors.$blackOpacity04,
      borderRadius: 16,
      height: 20,
      width: '100%',
    },
    $gteDesktop: {
      height: 24,
    },
  },
  lineName: {
    $base: {
      maxWidth: 220,
    },
  },
  lineAddress: {
    $base: {
      maxWidth: 220,
    },
  },
  lineStatus: {
    $base: {
      maxWidth: 209,
    },
  },
  // Stands in for the `(i)` and favourite icons, which grow a little on desktop.
  icon: {
    $base: {
      backgroundColor: tokens.colors.$blackOpacity04,
      borderRadius: 100,
      height: 20,
      width: 20,
    },
    $gteDesktop: {
      height: 22,
      width: 22,
    },
  },
  cta: {
    $base: {
      backgroundColor: tokens.colors.$blackOpacity04,
      borderRadius: 16,
      height: 31,
      width: 93,
    },
    $gteDesktop: {
      height: 33,
    },
  },
});
